class MyonCartDrawer {
  constructor() {
    this.drawer = document.getElementById('Myon-CartDrawer');
    if (!this.drawer) return;

    this.itemsContainer = document.getElementById('Myon-CartItems');
    this.promotionContainer = document.getElementById('Myon-CartPromotion');
    this.recommendationsContainer = document.getElementById('Myon-CartRecommendations');
    this.footer = document.getElementById('Myon-CartFooter');
    this.updateStatus = document.getElementById('Myon-CartUpdateStatus');

    this.updateStatusStartedAt = 0;
    this.minimumUpdateStatusTime = 350;
    this.cart = null;
    this.recommendationCache = {};
    this.currentRecommendationProductId = null;

    this.handleClickBound = this.handleClick.bind(this);
    this.handleKeydownBound = this.handleKeydown.bind(this);

    this.init();
  }

  /* =========================
     INIT
  ========================= */

  init() {
    document.addEventListener('click', this.handleClickBound);
    document.addEventListener('keydown', this.handleKeydownBound);
  }

  destroy() {
    document.removeEventListener('click', this.handleClickBound);
    document.removeEventListener('keydown', this.handleKeydownBound);
  }

  handleKeydown(event) {
    if (
      event.key === 'Escape' &&
      this.drawer.classList.contains('is-open')
    ) {
      this.close();
    }
  }

  /* =========================
     MONEY
  ========================= */

  money(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency
      }).format(cents / 100);
    } catch (error) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* =========================
     DISCOUNT
  ========================= */

  getDiscountPercent(quantity) {
    if (quantity >= 5) return 20;
    if (quantity >= 3) return 15;
    if (quantity >= 2) return 10;
    return 0;
  }

  getPromotionMessage(quantity) {
    if (quantity >= 5) {
      return `🎉 You've unlocked <strong>20% OFF</strong>`;
    }

    if (quantity === 4) {
      return `Add <strong>1 more item</strong> to save 20%`;
    }

    if (quantity === 3) {
      return `Add <strong>2 more items</strong> to save 20%`;
    }

    if (quantity === 2) {
      return `Add <strong>1 more item</strong> for 15% OFF`;
    }

    return `Add <strong>1 more item</strong> to unlock 10% OFF`;
  }

  getProgress(quantity) {
    if (quantity <= 0) return 0;
    if (quantity === 1) return 15;
    if (quantity === 2) return 30;
    if (quantity === 3) return 62;
    if (quantity === 4) return 80;
    return 100;
  }

  /* =========================
     OPEN / CLOSE
  ========================= */

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('myon-cart-open');
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('myon-cart-open');
  }

  /* =========================
     UPDATE STATUS
  ========================= */

  showUpdateStatus() {
    if (!this.updateStatus) return;

    this.updateStatusStartedAt = performance.now();
    this.updateStatus.hidden = false;
  }

  async hideUpdateStatus() {
    if (!this.updateStatus) return;

    const elapsed = performance.now() - this.updateStatusStartedAt;
    const remaining = this.minimumUpdateStatusTime - elapsed;

    if (remaining > 0) {
      await new Promise(resolve => setTimeout(resolve, remaining));
    }

    this.updateStatus.hidden = true;
  }

  /* =========================
     CART API
  ========================= */

  async getCart() {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart.js`, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    return response.json();
  }

  /* =========================
     REFRESH
  ========================= */

  async refresh(options = {}) {
    const { recommendations = true } = options;

    try {
      this.cart = await this.getCart();

      this.renderPromotion();
      this.renderItems();
      this.renderFooter();
      this.updateThemeCartCount();

      if (recommendations) {
        await this.renderRecommendations();
      }
    } catch (error) {
      console.error('Myon Cart Drawer:', error);
    }
  }

  /*
   * Called by product-form.js AFTER Shopify successfully adds an item.
   * Myon does NOT add anything itself.
   */
  async afterProductAdded() {
    try {
      this.open();
      this.showUpdateStatus();

      this.currentRecommendationProductId = null;

      await this.refresh({
        recommendations: true
      });
    } catch (error) {
      console.error('Myon afterProductAdded:', error);
    } finally {
      await this.hideUpdateStatus();
    }
  }

  /* =========================
     PROMOTION
  ========================= */

  renderPromotion() {
    if (!this.cart) return;

    const quantity = this.cart.item_count;

    if (quantity === 0) {
      this.promotionContainer.innerHTML = '';
      return;
    }

    const progress = this.getProgress(quantity);
    const milestone2 = quantity >= 2 ? 'active' : '';
    const milestone3 = quantity >= 3 ? 'active' : '';
    const milestone5 = quantity >= 5 ? 'active' : '';

    this.promotionContainer.innerHTML = `
      <div class="myon-promo">
        <div class="myon-promo-message">
          ${this.getPromotionMessage(quantity)}
        </div>

        <div class="myon-promo-progress-wrap">
          <div class="myon-promo-line">

            <div
              class="myon-promo-line-fill"
              style="width:${progress}%"
            ></div>

            <div class="myon-promo-milestone m1 ${milestone2}">
              ${quantity >= 2 ? '✓' : '2'}
            </div>

            <div class="myon-promo-milestone m2 ${milestone3}">
              ${quantity >= 3 ? '✓' : '3'}
            </div>

            <div class="myon-promo-milestone m3 ${milestone5}">
              ${quantity >= 5 ? '✓' : '5'}
            </div>

          </div>

          <div class="myon-promo-labels">
            <div class="myon-promo-label">10% OFF</div>
            <div class="myon-promo-label">15% OFF</div>
            <div class="myon-promo-label">20% OFF</div>
          </div>
        </div>
      </div>
    `;
  }

  /* =========================
     CART ITEMS
  ========================= */

  renderItems() {
    if (!this.cart || !this.cart.items.length) {
      this.itemsContainer.innerHTML = `
        <div class="myon-cart-empty">
          <div class="myon-cart-empty-title">
            Your cart is empty
          </div>

          <div>
            Add something you love.
          </div>
        </div>
      `;

      return;
    }

    this.itemsContainer.innerHTML = this.cart.items
      .map((item, index) => {
        const image =
          item.featured_image?.url ||
          item.image ||
          '';

        let variant = '';

        if (
          item.variant_title &&
          item.variant_title !== 'Default Title'
        ) {
          variant = `
            <div class="myon-cart-item-variant">
              ${this.escapeHTML(item.variant_title)}
            </div>
          `;
        }

        return `
          <div
            class="myon-cart-item"
            data-line="${index + 1}"
          >

            ${
              image
                ? `
                  <img
                    class="myon-cart-item-image"
                    src="${this.resizeImage(image, 180)}"
                    alt=""
                    loading="lazy"
                  >
                `
                : `<div class="myon-cart-item-image"></div>`
            }

            <div class="myon-cart-item-main">

              <div class="myon-cart-item-title">
                ${this.escapeHTML(item.product_title)}
              </div>

              ${variant}

              <div class="myon-cart-item-bottom">

                <div class="myon-cart-qty-area">

                  <div class="myon-cart-qty">
                    <button
                      type="button"
                      data-myon-qty-minus
                      data-line="${index + 1}"
                      data-quantity="${item.quantity}"
                      aria-label="Decrease quantity"
                    >
                      −
                    </button>

                    <span>${item.quantity}</span>

                    <button
                      type="button"
                      data-myon-qty-plus
                      data-line="${index + 1}"
                      data-quantity="${item.quantity}"
                      aria-label="Increase quantity"
                    >
                      +
                    </button>
                  </div>

                  <button
                    type="button"
                    class="myon-cart-remove"
                    data-myon-remove
                    data-line="${index + 1}"
                    aria-label="Remove item"
                  >
                    ${this.getTrashIcon()}
                  </button>

                </div>

                <div class="myon-cart-item-price">
                  ${this.money(item.final_line_price)}
                </div>

              </div>
            </div>
          </div>
        `;
      })
      .join('');
  }

  /* =========================
     FOOTER
  ========================= */

  renderFooter() {
    if (!this.cart || this.cart.item_count === 0) {
      this.footer.innerHTML = '';
      return;
    }

    const percentage =
      this.getDiscountPercent(
        this.cart.item_count
      );

    const originalSubtotal =
      this.cart.items_subtotal_price;

    const estimatedSaving =
      Math.round(
        originalSubtotal *
        (percentage / 100)
      );

    const estimatedTotal =
      originalSubtotal -
      estimatedSaving;

    const paymentIcons =
      document
        .getElementById('Myon-PaymentIcons')
        ?.innerHTML || '';

    this.footer.innerHTML = `
      ${
        percentage > 0
          ? `
            <div class="myon-cart-summary-row myon-saving">
              <span>You saved</span>
              <span>-${this.money(estimatedSaving)}</span>
            </div>
          `
          : ''
      }

      <a
        class="myon-checkout-button"
        href="/checkout"
      >
        CHECKOUT&nbsp;&nbsp;•&nbsp;&nbsp;${this.money(estimatedTotal)}
      </a>

      <div class="myon-checkout-trust">
        <div class="myon-payment-icons-wrap">
          ${paymentIcons}
        </div>

        <div class="myon-trust-text">
          Trusted by 1,000+ Happy Customers
        </div>
      </div>
    `;
  }

  /* =========================
     RECOMMENDATIONS
  ========================= */

  async renderRecommendations() {
    if (!this.cart || !this.cart.items.length) {
      this.recommendationsContainer.innerHTML = '';
      this.currentRecommendationProductId = null;
      return;
    }

    const seedProduct =
      this.cart.items[0];

    const productId =
      Number(seedProduct.product_id);

    if (
      this.currentRecommendationProductId === productId &&
      this.recommendationsContainer.children.length
    ) {
      return;
    }

    this.currentRecommendationProductId =
      productId;

    this.recommendationsContainer.innerHTML = `
      <div class="myon-cart-recommendations-title">
        Customers Also Loved
      </div>

      <div class="myon-recommendations-loading"></div>
    `;

    try {
      let products;

      if (this.recommendationCache[productId]) {
        products =
          this.recommendationCache[productId];
      } else {
        const root =
          window.Shopify?.routes?.root || '/';

        const url =
          `${root}recommendations/products.json` +
          `?product_id=${productId}` +
          `&limit=10` +
          `&intent=related`;

        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(
            'Unable to load recommendations.'
          );
        }

        const data =
          await response.json();

        products =
          data.products || [];

        this.recommendationCache[productId] =
          products;
      }

      if (
        !this.cart ||
        !this.cart.items.length ||
        Number(this.cart.items[0].product_id) !== productId
      ) {
        return;
      }

      const cartProductIds =
        new Set(
          this.cart.items.map(
            item => Number(item.product_id)
          )
        );

      products = products
        .filter(
          product =>
            !cartProductIds.has(
              Number(product.id)
            )
        )
        .slice(0, 9);

      if (!products.length) {
        this.recommendationsContainer.innerHTML = '';
        return;
      }

      this.recommendationsContainer.innerHTML = `
        <div class="myon-cart-recommendations-title">
          Customers Also Loved
        </div>

        <div class="myon-recommendations-grid">
          ${
            products
              .map(product => {
                const variant =
                  product.variants?.find(
                    variant =>
                      variant.available
                  ) ||
                  product.variants?.[0];

                const image =
                  product.featured_image ||
                  product.images?.[0] ||
                  '';

                const productUrl =
                  product.url || '#';

                return `
                  <div class="myon-rec-card">

                    <a
                      href="${productUrl}"
                      class="myon-rec-link"
                    >

                      <div class="myon-rec-image-wrap">
                        ${
                          image
                            ? `
                              <img
                                class="myon-rec-image"
                                src="${this.resizeImage(image, 300)}"
                                alt="${this.escapeHTML(product.title)}"
                                loading="lazy"
                              >
                            `
                            : ''
                        }
                      </div>

                      <div class="myon-rec-title">
                        ${this.escapeHTML(product.title)}
                      </div>

                      ${
                        variant
                          ? `
                            <div class="myon-rec-price">
                              from ${this.money(variant.price)}
                            </div>
                          `
                          : ''
                      }

                    </a>
                  </div>
                `;
              })
              .join('')
          }
        </div>
      `;
    } catch (error) {
      console.error(
        'Myon recommendations:',
        error
      );

      this.recommendationsContainer.innerHTML = '';
      this.currentRecommendationProductId = null;
    }
  }

  /* =========================
     CHANGE LINE
  ========================= */

  async changeLine(line, quantity) {
    const root =
      window.Shopify?.routes?.root || '/';

    try {
      this.showUpdateStatus();

      const oldSeedProductId =
        this.cart?.items?.[0]?.product_id
          ? Number(
              this.cart.items[0].product_id
            )
          : null;

      const response = await fetch(
        `${root}cart/change.js`,
        {
          method: 'POST',
          credentials: 'same-origin',

          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },

          body: JSON.stringify({
            line: Number(line),
            quantity: Number(quantity)
          })
        }
      );

      if (!response.ok) {
        throw new Error(
          'Unable to update cart.'
        );
      }

      this.cart =
        await response.json();

      this.renderPromotion();
      this.renderItems();
      this.renderFooter();
      this.updateThemeCartCount();

      if (!this.cart.items.length) {
        this.recommendationsContainer.innerHTML = '';
        this.currentRecommendationProductId = null;
        return;
      }

      const newSeedProductId =
        Number(
          this.cart.items[0].product_id
        );

      if (
        oldSeedProductId !== newSeedProductId
      ) {
        this.currentRecommendationProductId = null;

        await this.renderRecommendations();
      }
    } catch (error) {
      console.error(
        'Myon cart change:',
        error
      );
    } finally {
      await this.hideUpdateStatus();
    }
  }

  /* =========================
     CLICK HANDLERS
  ========================= */

  async handleClick(event) {
    const close =
      event.target.closest(
        '[data-myon-cart-close]'
      );

    if (close) {
      this.close();
      return;
    }

    const plus =
      event.target.closest(
        '[data-myon-qty-plus]'
      );

    if (plus) {
      const quantity =
        Number(
          plus.dataset.quantity
        );

      await this.changeLine(
        plus.dataset.line,
        quantity + 1
      );

      return;
    }

    const minus =
      event.target.closest(
        '[data-myon-qty-minus]'
      );

    if (minus) {
      const quantity =
        Number(
          minus.dataset.quantity
        );

      await this.changeLine(
        minus.dataset.line,
        Math.max(
          0,
          quantity - 1
        )
      );

      return;
    }

    const remove =
      event.target.closest(
        '[data-myon-remove]'
      );

    if (remove) {
      await this.changeLine(
        remove.dataset.line,
        0
      );

      return;
    }

    const openButton =
      event.target.closest(
        '[data-myon-cart-open]'
      );

    if (openButton) {
      event.preventDefault();

      await this.refresh({
        recommendations: true
      });

      this.open();
    }
  }

  /* =========================
     CART COUNT
  ========================= */

  updateThemeCartCount() {
    if (!this.cart) return;

    const count =
      Number(
        this.cart.item_count || 0
      );

    document
      .querySelectorAll(
        '[data-cart-count], [data-header-cart-count]'
      )
      .forEach(element => {
        element.textContent =
          count;
      });

    document
      .querySelectorAll(
        '.cart-count-bubble'
      )
      .forEach(bubble => {
        const visibleCount =
          bubble.querySelector(
            'span:not(.visually-hidden)'
          );

        const hiddenCount =
          bubble.querySelector(
            '.visually-hidden'
          );

        if (visibleCount) {
          visibleCount.textContent =
            count;
        }

        if (hiddenCount) {
          hiddenCount.textContent =
            count === 1
              ? '1 item'
              : `${count} items`;
        }

        if (count === 0) {
          bubble.hidden = true;
          bubble.style.display = 'none';
        } else {
          bubble.hidden = false;
          bubble.style.removeProperty(
            'display'
          );
        }
      });

    document
      .querySelectorAll(
        '.cart-count'
      )
      .forEach(element => {
        if (
          !element.classList.contains(
            'cart-count-bubble'
          )
        ) {
          element.textContent =
            count;
        }
      });
  }

  /* =========================
     ICONS
  ========================= */

  getTrashIcon() {
    return `
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <path
          d="M4 7H20"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />

        <path
          d="M9 4H15"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />

        <path
          d="M18 7L17.3 18.2C17.24 19.19 16.42 20 15.43 20H8.57C7.58 20 6.76 19.19 6.7 18.2L6 7"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />

        <path
          d="M10 11V16"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />

        <path
          d="M14 11V16"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
        />
      </svg>
    `;
  }

  /* =========================
     HELPERS
  ========================= */

  escapeHTML(value = '') {
    return String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  resizeImage(url, width) {
    if (!url) return '';

    try {
      const parsed =
        new URL(
          url,
          window.location.origin
        );

      parsed.searchParams.set(
        'width',
        width
      );

      return parsed.toString();
    } catch (error) {
      return url;
    }
  }
}

/* =========================
   START
========================= */

function initMyonCartDrawer() {
  if (
    window.myonCartDrawer?.destroy
  ) {
    window.myonCartDrawer.destroy();
  }

  window.myonCartDrawer =
    new MyonCartDrawer();
}

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    initMyonCartDrawer,
    { once: true }
  );
} else {
  initMyonCartDrawer();
}